import { lazy, Suspense, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Hero } from '../components/Hero';
import { scrollToId } from '../lib/scroll';
import { useI18n } from '../i18n';
import { HOME_FAQS } from '../content/homeFaqs';
import { SERVICES } from '../content/services';
import { CITIES } from '../content/cities';
import {
  breadcrumbJsonLd,
  collectionJsonLd,
  faqJsonLd,
  useSeo,
  webPageJsonLd,
} from '../lib/seo';
import { withLocale } from '../lib/localePath';

const HomeBelowFold = lazy(() =>
  import('./HomeBelowFold').then((m) => ({ default: m.HomeBelowFold }))
);

export function HomePage() {
  const { hash } = useLocation();
  const { t, locale } = useI18n();
  const path = withLocale(locale, '/');
  const faqs = HOME_FAQS[locale];

  const jsonLd = useMemo(
    () => [
      webPageJsonLd({
        name: t.meta.title,
        description: t.meta.description,
        path,
      }),
      breadcrumbJsonLd([{ name: 'Codex Algeria', path }]),
      collectionJsonLd({
        name:
          locale === 'fr'
            ? 'Services et villes Codex Algérie'
            : locale === 'ar'
              ? 'خدمات ومدن كودكس الجزائر'
              : 'Codex Algeria services and cities',
        description: t.meta.description,
        path,
        items: [
          ...SERVICES.map((s) => ({
            name: s.locales[locale].h1,
            path: withLocale(locale, `/services/${s.slug}`),
          })),
          ...CITIES.map((c) => ({
            name: c.locales[locale].h1,
            path: withLocale(locale, `/villes/${c.slug}`),
          })),
          {
            name:
              locale === 'fr'
                ? 'Devis projet digital'
                : locale === 'ar'
                  ? 'عرض سعر مشروع'
                  : 'Project quote',
            path: withLocale(locale, '/devis'),
          },
        ],
      }),
      faqJsonLd(faqs),
    ],
    [t.meta.title, t.meta.description, path, faqs, locale]
  );

  useSeo({
    title: t.meta.title,
    description: t.meta.description,
    path,
    locale,
    keywords:
      locale === 'fr'
        ? 'agence digitale Alger, développement logiciel Algérie, application mobile Alger, création site web Alger, Codex Algérie'
        : locale === 'ar'
          ? 'شركة برمجيات الجزائر, تطوير تطبيقات الجزائر, وكالة رقمية الجزائر العاصمة, كودكس'
          : 'software development Algeria, IT agency Algiers, web development Algeria, mobile app development Algiers, Codex Algeria',
    jsonLd,
  });

  useEffect(() => {
    void import('./HomeBelowFold');
  }, []);

  useEffect(() => {
    if (!hash) return;
    const id = hash.replace('#', '');
    let cancelled = false;
    void import('./HomeBelowFold').then(() => {
      if (cancelled) return;
      window.setTimeout(() => scrollToId(id), 50);
    });
    return () => {
      cancelled = true;
    };
  }, [hash]);

  return (
    <main>
      <Hero />
      <Suspense fallback={null}>
        <HomeBelowFold />
      </Suspense>
    </main>
  );
}
